import React from 'react';
import {
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';

import { Grid, Item } from './components';
import { IApp, ILauncherContext } from 'types';
import { styled } from '@mui/system';
import { red, grey } from '@mui/material/colors';
import { DARK } from 'lib/config';
import { useInitProvider } from 'providers/InitProvider';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@mui/icons-material';

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  '.homelink': {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '.MuiTypography-root': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
}));

const WrapperInt = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 10px',
}));

const WrapperIntMobile = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 5px',
}));

const CloseRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const filterAppEsterne = (app) => app.type === 'ext';
const filterAppInterne = (app) => app.type === 'int';

const LauncherView = ({
  data,
  type,
  gotoHomeHandler,
  logoHandler,
  defaultApp,
  workspaceSelected,
}: {
  data: ILauncherContext;
  type: string;
  defaultApp: IApp;
  logoHandler: (data: IApp) => void;
  gotoHomeHandler?: () => void;
  workspaceSelected: boolean;
}) => {
  const { apps } = data;

  const matches = useMediaQuery('(max-width:599px)');
  const initData = useInitProvider();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>();

  const externalApps = apps.filter(filterAppEsterne);

  const internalApps = apps.filter(filterAppInterne);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'launcher-popover' : undefined;
  const { t } = useTranslation();
  if (workspaceSelected) {
    return <></>;
  }

  const WrapperComp = matches ? WrapperIntMobile : WrapperInt;

  return (
    <>
      <WrapperComp>
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          sx={{ border: 0, background: open ? '#fff' : 'transparent' }}
        >
          <GridViewIcon
            sx={{ color: open ? '#1A416B' : type === DARK ? grey[50] : red }}
          />
        </IconButton>
      </WrapperComp>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Wrapper direction="column" spacing={2} sx={{
          maxHeight: '500px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {matches && (
            <CloseRow>
              <Typography variant="subtitle2">
                {t('label.app.interne')}
              </Typography>
              <a
                href="#close"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                }}
                style={{ color: '#1A416B' }}
              >
                <CloseIcon />
              </a>
            </CloseRow>
          )}

          {!initData.amIUsingTheDefaultApp() ? (
            <>
              <Stack direction="row" spacing={2}>
                <div
                  className="homelink"
                  onClick={() => {
                    handleClose();
                    gotoHomeHandler();
                    logoHandler(defaultApp);
                  }}
                >
                  <HomeOutlinedIcon />{' '}
                  <Typography>{t('titles.home', 'Go to home')}</Typography>
                  <ArrowForwardOutlinedIcon />
                </div>
              </Stack>
              <Divider />
            </>
          ) : null}
          {apps && apps.length === 0 && (
            <div>
              <Grid>
                <Typography variant="subtitle2">
                  {t('noAppsMessage', 'Do you need help?')}
                </Typography>
              </Grid>
            </div>
          )}
          {internalApps.length > 0 ? (
            <div>
              <Grid>
                {internalApps.map((app) => (
                  <Item
                    data={app}
                    key={app.id}
                    closeHandler={handleClose}
                    logoHandler={logoHandler}
                  />
                ))}
              </Grid>
            </div>
          ) : null}
          {externalApps.length > 0 ? (
            <div>
              <Divider sx={{ marginBottom: '24px' }} />
              <Typography variant="subtitle2" sx={{ marginBottom: '24px' }}>
                {t('label.app.esterne')}
              </Typography>
              <Grid>
                {externalApps.map((app) => (
                  <Item
                    data={app}
                    key={app.id}
                    closeHandler={handleClose}
                    logoHandler={logoHandler}
                  />
                ))}
              </Grid>
            </div>
          ) : null}
        </Wrapper>
      </Popover>
    </>
  );
};

export default LauncherView;
